import {
  useFormState,
  useStoredAdviceCart,
} from "Store/priceCalculation/priceCalculationHooks";
import { Form, FormikProps } from "formik";
import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  useAdviceDetailsFormValues,
  useIsAdvicePriceLoading,
} from "../ProductPrice/hooks/stateSelectors";
import {
  AkaAdviceCheckboxes,
  AkaAdvicePriceFormValues,
  AkaAdvicePriceFormValuesWithAction,
  SubmitAction,
} from "./AkaAdviceCards/interfaces";
import {
  useAdvicePriceCalculationResults,
  useAkaAdviceResult,
} from "./hooks/stateSelectors";

import { WrapMdMarginBottom } from "App/global-style";
import { ProductPriceTile } from "Components/Features/CheckPrice/Tiles/ProductPriceTile";
import { useUserIdentification } from "Components/Features/CheckPrice/userIdentificationHook";
import { SystemErrorAlert } from "Components/Form/Alert/SystemErrorAlert";
import { NavigationButtons } from "Components/NavigationButtons";
import { ScrollToFirstError } from "Components/ScrollToFirstError";
import { useTexts } from "Components/TextContext";
import { Button } from "Components/TextContext/commonComponentEnums";
import { ThemedSpinner } from "Components/Themes/components/ThemedSpinner";
import { MicroAdviceProps } from "Remotes/advice/types";
import { addAkaAdviceProductsToCartStart } from "Store/addProductToCart/slice";
import { AdviceType } from "Store/advicePDF/interfaces";
import { useIsAdvisorSelector } from "Store/advisor/advisorSelectors";
import { useAdviceType } from "Store/akaAdvice/akaAdviceHooks";
import { updatePriceCalculationFormState } from "Store/priceCalculation/priceCalculationSlice";
import { ReduxState } from "Store/rootReducer";
import { getFMSProductName } from "Store/texts/helpers/FMSproductName";
import { useProductTexts } from "Store/texts/hooks";
import { isDefined } from "Utils/functions";
import { ADVICE_PRICE_STEP } from "..";
import { ProductBackendName } from "../../productTypes";
import { AddAdviceProductsToCartError } from "./AddAdviceProductsToCartError";
import { CollectiveAdviceForm } from "./AkaAdviceForm/CollectiveAdviceForm";
import { IndividualAdviceForm } from "./AkaAdviceForm/IndividualAdviceForm";
import { useAkaAdviceScreenshot } from "./AkaAdviceScreenshot/AkaAdviceScreenshot";
import { validationSchema } from "./validation";

type ProductPriceMicro = Pick<MicroAdviceProps, "onAdviceCompleted">;

export const ProductPriceAka = ({
  onAdviceCompleted,
}: ProductPriceMicro): React.ReactElement => {
  const dispatch = useDispatch();
  const { getText } = useTexts();
  const [
    {
      formText: { advice: adviceText },
    },
    lang,
  ] = useProductTexts(getFMSProductName(ProductBackendName.ADVICE));

  const adviceType = useAdviceType();
  const isAdvisor = useIsAdvisorSelector();
  const addProductToCart = useSelector(
    (state: ReduxState) => state.addProductToCart,
  );
  const priceError = useSelector(
    (state: ReduxState) => state.akaAdvice.advicePriceCalculationResults.error,
  );
  const isPriceLoading = useIsAdvicePriceLoading();
  const adviceDetails = useAdviceDetailsFormValues();
  const advicePriceCalculationResults = useAdvicePriceCalculationResults();
  const initialFormState = useFormState<AkaAdvicePriceFormValues>(
    ADVICE_PRICE_STEP.id,
    initialFormValues,
  );
  const collectiveAdvice = useAkaAdviceResult();
  const akaAdviceScreenshot = useAkaAdviceScreenshot();
  const { screenshotRef, takeScreenshot, setManualScreenshot } =
    akaAdviceScreenshot;

  useUserIdentification(ProductBackendName.ADVICE);
  useStoredAdviceCart();

  useEffect(() => {
    if (
      screenshotRef &&
      isDefined(adviceDetails) &&
      isDefined(collectiveAdvice) &&
      !isPriceLoading
    ) {
      takeScreenshot(true);
    }
  }, [screenshotRef]);

  const handleSubmit = ({
    submitAction,
    ...formProps
  }: AkaAdvicePriceFormValuesWithAction): void => {
    const currentScreenshotRef = takeScreenshot();
    if (submitAction === SubmitAction.DOWNLOAD_ADVICE) {
      setManualScreenshot(true);
    }
    dispatch(
      updatePriceCalculationFormState({
        stepId: ADVICE_PRICE_STEP.id,
        formState: formProps,
      }),
    );
    dispatch(
      addAkaAdviceProductsToCartStart({
        formProps,
        redirectToCheckout:
          !isAdvisor && submitAction !== SubmitAction.DOWNLOAD_ADVICE,
        takeScreenshot: submitAction === SubmitAction.DOWNLOAD_ADVICE,
        screenshotRef: currentScreenshotRef,
        onAdviceCompletedForAdvisor: onAdviceCompleted,
      }),
    );
  };

  const handleTakeScreenshot = (formProps: AkaAdvicePriceFormValues): void => {
    handleSubmit(formProps);
  };

  if (
    !isDefined(adviceDetails) ||
    !isDefined(collectiveAdvice) ||
    !isDefined(advicePriceCalculationResults)
  ) {
    return <ThemedSpinner />;
  }

  const doubleYearlyIncome = adviceDetails.income * 2;

  return (
    <ProductPriceTile
      initialValues={initialFormState}
      validationSchema={validationSchema(
        isAdvisor,
        doubleYearlyIncome,
        collectiveAdvice,
        adviceType,
        adviceText,
        lang,
      )}
      onSubmit={(formValues: AkaAdvicePriceFormValues) => {
        handleSubmit(formValues);
      }}
      header={"Anbefaling"}
      maxWidth="564px"
    >
      {(formProps: FormikProps<AkaAdvicePriceFormValues>) => {
        return (
          <Form>
            {adviceType === AdviceType.COLLECTIVE ? (
              <CollectiveAdviceForm
                formProps={formProps}
                collectiveAdvice={collectiveAdvice}
                akaAdviceScreenshot={akaAdviceScreenshot}
                handleTakeScreenshot={handleTakeScreenshot}
              />
            ) : (
              <IndividualAdviceForm
                formProps={formProps}
                collectiveAdvice={collectiveAdvice}
                akaAdviceScreenshot={akaAdviceScreenshot}
              />
            )}
            {priceError && <SystemErrorAlert alertId="priceCalculation" />}
            <AddAdviceProductsToCartError addProductToCart={addProductToCart} />
            <ScrollToFirstError />
            <WrapMdMarginBottom>
              <NavigationButtons
                customNextButtonText={
                  isAdvisor ? getText(Button.SAVE_IN_CART) : undefined
                }
                isLoading={addProductToCart.isLoading}
                disableNextButton={isPriceLoading || isDefined(priceError)}
              />
            </WrapMdMarginBottom>
          </Form>
        );
      }}
    </ProductPriceTile>
  );
};

export const initialFormValues: AkaAdvicePriceFormValues = {
  [AkaAdviceCheckboxes.DEATH_CHECKBOX]: false,
  akaGroupLifeDeathCheckbox: false,
  akaGroupLifeDeathGLevel: undefined,
  akaGroupLifeDeathCoInsuredCheckbox: undefined,
  akaGroupLifeDeathCoInsuredGLevel: undefined,
  deathInsuredSumCheckbox: false,
  deathInsuredSumSlider: undefined,
  deathAdviceComment: undefined,

  [AkaAdviceCheckboxes.DISABILITY_CHECKBOX]: false,
  incapacityOfWorkCheckbox: false,
  incapacityOfWorkGLevel: undefined,
  incapacityOfWorkCoInsuredCheckbox: undefined,
  incapacityOfWorkCoInsuredGLevel: undefined,
  timeLimitedDisabilityInterestCheckbox: false,
  timeLimitedDisabilityInterestGLevel: undefined,
  timeLimitedDisabilityInterestCoInsuredCheckbox: undefined,
  timeLimitedDisabilityInterestCoInsuredGLevel: undefined,
  disabilityInsuredSumCheckbox: false,
  disabilityInsuredSumSlider: undefined,
  disabilityPaymentAfter3Months: undefined,
  disabilityOneTimePaymentCheckbox: false,
  disabilityOneTimePaymentValue: undefined,
  disabilityOneTimePaymentAdvisorChoice: undefined,
  disabilityOneTimePaymentAdvisorValue: undefined,
  disabilityAdviceComment: undefined,

  [AkaAdviceCheckboxes.CRITICAL_ILLNESS_CHECKBOX]: false,
  akaCriticalIllnessCoverageAmountCheckbox: false,
  akaCriticalIllnessCoverageAmountGLevel: undefined,
  akaCriticalIllnessCoverageAmountCoInsuredCheckbox: undefined,
  akaCriticalIllnessCoverageAmountCoInsuredGLevel: undefined,
  criticalIllnessInsuredSumCheckbox: false,
  criticalIllnessInsuredSumSlider: undefined,
  criticalIllnessAdviceComment: undefined,
};
